import React from "react";
import RuleModal from "components/common/RuleModal";
import { Product, Room } from "module/types";

interface ProductGameHeaderProps {
  product: Product;
  room?: Room;
  roomName?: string;
  round?: number;
  leftTime?: number;
  isRuleOpen: boolean;
  setIsRuleOpen: (v: boolean) => void;
  isMemoOpen?: boolean;
  setIsMemoOpen?: (v: boolean) => void;
  isUserListOpen?: boolean;
  setIsUserListOpen?: (v: boolean) => void;
  isSettingOpen?: boolean;
  setIsSettingOpen?: (v: boolean) => void;
  userCount?: number;
  t: (key: string) => string;
  extraHeaderButtons?: React.ReactNode;
  children?: React.ReactNode;
}

const ProductGameHeader: React.FC<ProductGameHeaderProps> = ({
  product,
  room,
  roomName,
  round,
  leftTime,
  isRuleOpen,
  setIsRuleOpen,
  isMemoOpen,
  setIsMemoOpen,
  isUserListOpen,
  setIsUserListOpen,
  isSettingOpen,
  setIsSettingOpen,
  userCount,
  t,
  extraHeaderButtons,
  children,
}) => {
  return (
    <>
      <div className="product-game-header fixed top-0 left-0 right-0 px-3 flex items-center z-50 min-h-[56px]">
        {/* Left section - Round info */}
        <div className="flex items-center space-x-2">
          {typeof round === "number" && (
            <div className="info-badge px-3 py-1.5 rounded-lg text-sm flex items-center">
              <i className="fas fa-flag-checkered mr-2" />
              <span className="label mr-1">{t("round")}:</span>
              <span className="value font-medium">{round < 0 ? 0 : round}</span>
            </div>
          )}
        </div>

        {/* Center section - Timer (large) */}
        <div className="absolute left-1/2 transform -translate-x-1/2">
          {typeof leftTime === "number" && (
            <div
              className={`timer-badge flex items-center justify-center ${
                leftTime < 10 ? "timer-warning" : ""
              }`}
            >
              <i className="fas fa-clock mr-3" />
              <span className="timer-value">{leftTime}</span>
            </div>
          )}
        </div>

        {/* Right section - Action buttons */}
        <div className="ml-auto flex items-center space-x-1">
          {/* Extra header buttons (e.g., role settings) */}
          {extraHeaderButtons}

          <div
            className="icon-button px-2 py-1 rounded-lg text-sm flex items-center cursor-pointer min-w-[36px] min-h-[32px] justify-center transition-colors duration-200"
            onClick={() => {
              if (setIsMemoOpen) setIsMemoOpen(false);
              if (setIsSettingOpen) setIsSettingOpen(false);
              setIsRuleOpen(!isRuleOpen);
            }}
          >
            <i className="fas fa-book md:text-xl" />
          </div>
          {setIsMemoOpen && (
            <div
              className="icon-button px-2 py-1 rounded-lg text-sm flex items-center cursor-pointer min-w-[36px] min-h-[32px] justify-center transition-colors duration-200"
              onClick={() => {
                setIsRuleOpen(false);
                if (setIsSettingOpen) setIsSettingOpen(false);
                setIsMemoOpen && setIsMemoOpen(!isMemoOpen);
              }}
            >
              <i className="fa-solid fa-pen-to-square md:text-xl" />
            </div>
          )}
          {setIsUserListOpen && (
            <div
              className="icon-button px-2 py-1 rounded-lg text-sm flex items-center cursor-pointer relative min-w-[36px] min-h-[32px] justify-center transition-colors duration-200"
              onClick={() => setIsUserListOpen(!isUserListOpen)}
            >
              <i className="fas fa-users md:text-xl" />
              {typeof userCount === "number" && userCount > 0 && (
                <div className="badge-count absolute -top-1 -right-1 text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold leading-none">
                  {userCount > 99 ? "99+" : userCount}
                </div>
              )}
            </div>
          )}
          {setIsSettingOpen && (
            <div
              className="icon-button px-2 py-1 rounded-lg text-sm flex items-center cursor-pointer min-w-[36px] min-h-[32px] justify-center transition-colors duration-200"
              onClick={() => {
                setIsRuleOpen(false);
                if (setIsMemoOpen) setIsMemoOpen(false);
                setIsSettingOpen(!isSettingOpen);
              }}
            >
              <i className="fas fa-cog md:text-xl" />
            </div>
          )}
        </div>
        <RuleModal
          isOpen={isRuleOpen}
          recreationId={product.id}
          inGame={true}
        />
      </div>

      {/* Settings Menu with Room Info */}
      {isSettingOpen && (room || roomName) && (
        <div className="fixed right-2 top-[60px] w-64 rounded-lg shadow-xl z-40 border border-gray-700 overflow-hidden setting-menu">
          {/* Room Info Box */}
          <div className="bg-gray-800 px-4 py-2.5">
            <div className="text-sm font-semibold text-white mb-2">
              {t(product.title + ".title")}
            </div>

            <div className="text-xs text-gray-400 mb-0.5">{t("room")}</div>
            <div className="text-sm font-semibold text-white">
              {room?.roomName || roomName}
            </div>

            {room?.roomPassword && room.roomPassword.length > 0 && (
              <>
                <div className="text-xs text-gray-400 mb-0.5 mt-2">
                  {t("password")}
                </div>
                <div className="text-sm font-semibold text-white">
                  {room.roomPassword}
                </div>
              </>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-700"></div>

          {/* Children (Leave Room button) rendered inside settings menu */}
          {isSettingOpen && children}
        </div>
      )}
    </>
  );
};

export default ProductGameHeader;
